import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18n } from '../../../contexts/I18nContext';
import { formatWeekRange } from './weeklyWeek';

interface Props {
  weekStart: string;
  weekEnd: string;
  /** Disables "next week" on the current week — a future week has no activity. */
  isCurrentWeek: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday?: () => void;
  className?: string;
}

/**
 * The week picker, shaped like the Daily Summary's date field: a labelled
 * field in the filter row (same trigger geometry, border and height as every
 * dropdown) with ◀ week range ▶ inside, instead of centred buttons under the
 * header.
 */
export function WeeklyWeekNav({
  weekStart,
  weekEnd,
  isCurrentWeek,
  onPrev,
  onNext,
  onToday,
  className = '',
}: Props) {
  const { t } = useI18n();
  // The button is the touch target (44px on phones, index.css) and spans the
  // field's full height; the highlight is the small square around the icon,
  // so it stays centred whatever size the target gets.
  const arrow =
    'group -my-2 inline-flex items-center justify-center self-stretch disabled:cursor-not-allowed disabled:opacity-40';
  const arrowIcon =
    'rounded p-0.5 text-gray-400 group-active:bg-accent group-active:text-foreground group-hover:bg-accent group-hover:text-foreground';
  return (
    <div className={className}>
      <span className="ui-field-label">{t('weekly.weekLabel')}</span>
      <div className="ui-select-trigger cursor-default">
        <button type="button" onClick={onPrev} className={arrow} aria-label={t('weekly.prevWeek')}>
          <span className={arrowIcon}>
            <ChevronLeft size={16} />
          </span>
        </button>
        <button
          type="button"
          onClick={onToday}
          disabled={!onToday || isCurrentWeek}
          title={isCurrentWeek ? undefined : t('weekly.thisWeek')}
          className="-my-2 min-w-0 flex-1 self-stretch truncate text-center tabular-nums disabled:cursor-default"
        >
          {formatWeekRange(weekStart, weekEnd)}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={isCurrentWeek}
          className={arrow}
          aria-label={t('weekly.nextWeek')}
        >
          <span className={arrowIcon}>
            <ChevronRight size={16} />
          </span>
        </button>
      </div>
    </div>
  );
}
